import { forwardRef, useEffect, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactElement, type ReactNode, type SVGProps } from "react";

export type ThemePreference = "system" | "light" | "dark";
export type UiButtonTone = "primary" | "secondary" | "quiet" | "danger";
export type UiButtonSize = "sm" | "md" | "lg";
export type UiButtonState = "default" | "hover" | "focus" | "disabled" | "loading" | "error";
export type UiStatusTone = "error" | "success" | "warning" | "empty";

const THEME_STORAGE_KEY = "kuroo-theme-preference";

function systemTheme(): "light" | "dark" {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

export function applyThemePreference(preference: ThemePreference = readThemePreference(), root: HTMLElement | null = typeof document === "undefined" ? null : document.documentElement): ThemePreference {
  if (!root) return preference;
  const resolved = preference === "system" ? systemTheme() : preference;
  root.dataset.themePreference = preference;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  return preference;
}

export function setThemePreference(preference: ThemePreference): void {
  if (typeof window !== "undefined") window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  applyThemePreference(preference);
}

export function useThemePreference(): [ThemePreference, (preference: ThemePreference) => void] {
  const [preference, setPreference] = useState<ThemePreference>(() => readThemePreference());
  useEffect(() => {
    applyThemePreference(preference);
    if (preference !== "system" || typeof window === "undefined") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => applyThemePreference("system");
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, [preference]);
  return [preference, (next) => { setPreference(next); setThemePreference(next); }];
}

export function BrandLockup({ businessName, productName = "Kuroo", logoUrl, compact = false }: { businessName?: string | null; productName?: string | null; logoUrl?: string | null; compact?: boolean }): ReactElement {
  const label = businessName || productName || "Kuroo";
  return <span className={`brand-lockup${compact ? " brand-lockup-compact" : ""}`} aria-label={label}>
    <span className="brand-lockup-mark">{logoUrl ? <img src={logoUrl} alt="" /> : <span aria-hidden="true">K</span>}</span>
    <span className="brand-lockup-copy"><strong>{label}</strong>{!compact && <small>{productName && productName !== label ? productName : "Reservas con intención"}</small>}</span>
  </span>;
}

export function ThemeToggle({ compact = false, label = "Tema" }: { compact?: boolean; label?: string }): ReactElement {
  const [preference, setPreference] = useThemePreference();
  const next: ThemePreference = preference === "system" ? "dark" : preference === "dark" ? "light" : "system";
  const labelByPreference: Record<ThemePreference, string> = { system: "Sistema", light: "Claro", dark: "Oscuro" };
  return <button className="theme-toggle" type="button" onClick={() => setPreference(next)} aria-label={`${label}: ${labelByPreference[preference]}. Cambiar`} title={`${label}: ${labelByPreference[preference]}`}>
    <SvgIcon name={preference === "dark" ? "moon" : preference === "light" ? "sun" : "spark"} size={17} />{!compact && <span>{labelByPreference[preference]}</span>}
  </button>;
}

export const UiButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { tone?: UiButtonTone; size?: UiButtonSize; state?: UiButtonState; loading?: boolean }>(function UiButton({ tone = "primary", size = "md", state, loading = false, className = "", children, disabled = false, ...props }, ref): ReactElement {
  const isLoading = loading || state === "loading";
  const isDisabled = disabled || isLoading || state === "disabled";
  const stateClass = state ? ` ui-button-state-${state}` : "";
  return <button {...props} ref={ref} type={props.type ?? "button"} disabled={isDisabled} aria-busy={isLoading || undefined} aria-disabled={isDisabled || undefined} data-state={state} className={`ui-button ui-button-${tone} ui-button-${size}${isLoading ? " ui-button-loading" : ""}${stateClass}${className ? ` ${className}` : ""}`}>
    {isLoading && <span className="ui-button-spinner" aria-hidden="true" />}
    {children}
  </button>;
});

export type UiInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
};

/**
 * Shared labelled input primitive. The wrapper owns the accessible relation
 * between label, hint and error so product surfaces do not reimplement it.
 * Consumers can still pass every native input attribute and a custom id.
 */
export const UiInput = forwardRef<HTMLInputElement, UiInputProps>(function UiInput({ label, hint, error, id, className = "", containerClassName = "", required, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, ...props }, ref): ReactElement {
  const generatedId = useId();
  const inputId = id ?? `kuroo-input-${generatedId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [ariaDescribedBy, hintId, errorId].filter(Boolean).join(" ");
  return <div className={`ui-field${containerClassName ? ` ${containerClassName}` : ""}`}>
    {label && <label className="ui-field-label" htmlFor={inputId}>{label}{required && <span className="ui-field-required" aria-hidden="true"> *</span>}</label>}
    <input {...props} ref={ref} id={inputId} required={required} aria-invalid={error ? true : ariaInvalid} aria-describedby={describedBy || undefined} data-error={error ? "true" : undefined} className={`ui-input${className ? ` ${className}` : ""}`} />
    {hint && !error && <span className="ui-field-hint" id={hintId}>{hint}</span>}
    {error && <span className="ui-field-error" id={errorId} role="alert">{error}</span>}
  </div>;
});

export function UiKpiCard({ label, value, delta, children, className = "" }: { label: string; value: string | number; delta?: string; children?: ReactNode; className?: string }): ReactElement {
  return <article className={`ui-kpi-card${className ? ` ${className}` : ""}`}>
    <div className="ui-kpi-card-heading"><span>{label}</span><span className="ui-kpi-card-icon" aria-hidden="true">↗</span></div>
    <strong className="ui-kpi-card-value">{value}</strong>
    {delta && <span className="ui-kpi-card-delta">{delta}</span>}
    {children}
  </article>;
}

export function UiFilterBar({ children, className = "" }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={`ui-filter-bar${className ? ` ${className}` : ""}`}>{children}</div>;
}

export type UiTabItem = { id: string; label: ReactNode; disabled?: boolean };

export function UiTabs({ items, value, onChange, label, className = "", idPrefix, panelId }: { items: readonly UiTabItem[]; value: string; onChange: (value: string) => void; label: string; className?: string; idPrefix?: string; panelId?: string }): ReactElement {
  return <div className={`ui-tabs${className ? ` ${className}` : ""}`} role="tablist" aria-label={label}>
    {items.map((item) => {
      const selected = item.id === value;
      return <button key={item.id} id={idPrefix ? `${idPrefix}-${item.id}` : undefined} type="button" role="tab" aria-selected={selected} aria-controls={panelId} disabled={item.disabled} className={selected ? "active" : ""} onClick={() => onChange(item.id)}>{item.label}</button>;
    })}
  </div>;
}

export function UiIconButton({ label, title = label, tone = "quiet", size = "sm", className = "", children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; title?: string; tone?: UiButtonTone; size?: UiButtonSize; state?: UiButtonState; loading?: boolean }): ReactElement {
  return <UiButton {...props} tone={tone} size={size} className={`ui-icon-button${className ? ` ${className}` : ""}`} aria-label={label} title={title}>{children}</UiButton>;
}

export function UiStepper({ steps, currentStep, label = "Pasos", className = "" }: { steps: string[]; currentStep: number; label?: string; className?: string }): ReactElement {
  return <ol className={`ui-stepper${className ? ` ${className}` : ""}`} aria-label={label}>
    {steps.map((step, index) => <li key={step} className={index === currentStep ? "active" : index < currentStep ? "complete" : ""} aria-current={index === currentStep ? "step" : undefined}><span>{index + 1}</span>{step}</li>)}
  </ol>;
}

export function UiStatus({ tone, children, className = "" }: { tone: UiStatusTone; children: ReactNode; className?: string }): ReactElement {
  return <div className={`ui-status ui-status-${tone}${className ? ` ${className}` : ""}`} role={tone === "error" ? "alert" : "status"}>{children}</div>;
}

const iconPaths: Record<string, ReactElement> = {
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></>,
  moon: <path d="M20.2 15.6A8.7 8.7 0 0 1 8.4 3.8 8.7 8.7 0 1 0 20.2 15.6Z" />,
  spark: <><path d="m12 3 1.45 5.55L19 10l-5.55 1.45L12 17l-1.45-5.55L5 10l5.55-1.45L12 3Z" /><path d="m19 17 .55 2.45L22 20l-2.45.55L19 23l-.55-2.45L16 20l2.45-.55L19 17Z" /></>,
  arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
  calendar: <><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M7 2.5v4M17 2.5v4M3 9h18" /></>,
  pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
  search: <><circle cx="10.8" cy="10.8" r="6.8" /><path d="m16 16 5 5" /></>,
  chat: <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-5.5 4v-4.17A2.5 2.5 0 0 1 4 12.5v-7Z" />,
  check: <path d="m5 12 4.5 4.5L19 7" />,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />
};

export function SvgIcon({ name, size = 20, ...props }: { name: keyof typeof iconPaths; size?: number } & Omit<SVGProps<SVGSVGElement>, "name">): ReactElement {
  return <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{iconPaths[name]}</svg>;
}
